import { useMemo } from 'react'

import { useSelector } from 'react-redux'

import { useEntitlement } from '@webapp/core/hooks/useEntitlement'
import { makeSelectHeadquartersTimezone } from '@webapp/core/stores/global'
import {
  useCalculateBudgetSubtotals,
  useFeatureFlag,
  useGetBudgetBreakdown,
} from '@webapp/hooks'
import { formatBudgetCategories } from './utils/formatBudgetCategories'
import { formatBudgetCategoriesDeprecated } from './utils/formatBudgetCategoriesDeprecated'
import { formatSecondaryCategorySpend } from './utils/formatSecondaryCategorySpend'
import { useQueryBudgetCategoriesForExpense } from '../../models/useQueryBudgetCategoriesForExpense'
import { useQueryExpenseDetail } from '../../models/useQueryExpenseDetail'
import { useQuerySpendBySecondaryCategory } from '../../models/useQuerySpendBySecondaryCategory'

interface IUseSpendTrackerPresenterProps {
  expenseUuid: string
}

export const useSpendTrackerPresenter = ({
  expenseUuid,
}: IUseSpendTrackerPresenterProps) => {
  const headquartersTimezone = useSelector(makeSelectHeadquartersTimezone())
  const FT_5817_ENABLE_BUDGETS_REDESIGN = useFeatureFlag(
    'FT_5817_ENABLE_BUDGETS_REDESIGN'
  )

  const { expense } = useQueryExpenseDetail(expenseUuid)

  const entitlement = useEntitlement()
  const isSpendTrackerEnabled = Boolean(
    entitlement?.module.budgets.enabled && expense?.permissions.canApprove
  )

  const {
    budgetCategories,
    uncategorizedSpend,
    isLoading: isBudgetCategoriesLoading,
    isError: wasErrorFetchingBudgetCategories,
  } = useQueryBudgetCategoriesForExpense({
    expenseUuid,
    expenseId: expense?.id,
    enabled: isSpendTrackerEnabled,
  })

  // Pre-redesign data...This can be removed when removing FT_5817_ENABLE_BUDGETS_REDESIGN
  const getBudgetBreakdown = useGetBudgetBreakdown()
  const formattedBudgetCategories = useMemo(() => {
    if (!budgetCategories || FT_5817_ENABLE_BUDGETS_REDESIGN) return []
    return formatBudgetCategoriesDeprecated({
      budgetCategories,
      headquartersTimezone,
      getBudgetBreakdown,
    })
  }, [
    budgetCategories,
    FT_5817_ENABLE_BUDGETS_REDESIGN,
    headquartersTimezone,
    getBudgetBreakdown,
  ])

  // Post-redesign data...
  const calculateBudgetSpendDetails = useCalculateBudgetSubtotals()
  const formattedBudgetData = useMemo(() => {
    if (!budgetCategories || !FT_5817_ENABLE_BUDGETS_REDESIGN) return []
    return formatBudgetCategories({
      budgetCategories,
      headquartersTimezone,
      calculateBudgetSpendDetails,
    })
  }, [
    budgetCategories,
    FT_5817_ENABLE_BUDGETS_REDESIGN,
    headquartersTimezone,
    calculateBudgetSpendDetails,
  ])

  // If budgetCategories is empty or there was an error fetching budgetCategories, we fall back to showing
  // the secondary category spend
  const showSecondaryCategorySpend =
    (budgetCategories?.length === 0 || wasErrorFetchingBudgetCategories) &&
    !!expense
  const currentYear = new Date().getFullYear()
  const startDate = `${currentYear}-01-01`
  const endDate = `${currentYear}-12-31`

  const { secondaryCategorySpend, isLoading: isSecondaryCategorySpendLoading } =
    useQuerySpendBySecondaryCategory({
      expenseUuid,
      secondaryCategoryId: expense?.secondaryCategory.id,
      expenseId: expense?.id,
      enabled: showSecondaryCategorySpend,
      startDate,
      endDate,
    })
  const formattedSecondaryCategorySpend = useMemo(() => {
    if (!secondaryCategorySpend || !showSecondaryCategorySpend) return undefined
    return formatSecondaryCategorySpend({
      secondaryCategorySpend: {
        approved: secondaryCategorySpend.approvedSpend ?? '0',
        currentRequestPending:
          secondaryCategorySpend.currentRequestPendingSpend ?? '0',
        invoiced: secondaryCategorySpend.invoicedSpend ?? '0',
        pending: secondaryCategorySpend.pendingSpend ?? '0',
        purchased: secondaryCategorySpend.purchasedSpend ?? '0',
        received: secondaryCategorySpend.receivedSpend ?? '0',
      },
      startDate,
      endDate,
      headquartersTimezone,
    })
  }, [
    secondaryCategorySpend,
    showSecondaryCategorySpend,
    startDate,
    endDate,
    headquartersTimezone,
  ])

  return {
    data: {
      budgetCategories: formattedBudgetCategories,
      processedBudgetData: formattedBudgetData,
      primaryCategory: expense?.primaryCategory,
      secondaryCategory: expense?.secondaryCategory,
      secondaryCategorySpend: formattedSecondaryCategorySpend,
      headquartersTimezone,
      uncategorizedSpend,
    },
    states: {
      isSpendTrackerDataLoading:
        isBudgetCategoriesLoading || isSecondaryCategorySpendLoading,
      isSpendTrackerEnabled,
    },
  }
}
